// src/App.jsx
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './components/LoginPage';
import RegistrationDashboard from './components/dashboard/RegistrationDashboard';
import TerminalDashboard from './components/dashboard/TerminalDashboard';
import ConsultationPage from './components/ConsultationPage';
import DashboardLayout from './components/dashboard/DashboardLayout';
import PublicLayout from './components/public/PublicLayout';
import LandingPage from './components/public/LandingPage';
import InsurancePageGE from './components/insurance/InsurancePageGE';
import InsurancePage from './components/insurance/InsurancePage';
import VehicleFleetPage from './components/dashboard/VehicleFleetPage';
import InvoicePage from './components/invoices/InvoicePage';
import QueueToRussiaPage from './components/queue/QueueToRussiaPage';
import AdminLayout from './components/adminpanel/AdminLayout';
import AdminDashboard from './components/adminpanel/AdminDashboard';
import AdminRegistrations from './components/adminpanel/AdminRegistrations';
import AdminInvoices from './components/adminpanel/AdminInvoices';
import AdminInsurance from './components/adminpanel/AdminInsurance';
import AdminPreorders from './components/adminpanel/AdminPreorders';
import AdminVehicleFleet from './components/adminpanel/AdminVehicleFleet';
import AdminHistory from './components/adminpanel/AdminHistory';
import { LanguageProvider } from './contexts/LanguageContext';
import { NotificationProvider } from './contexts/NotificationContext';
import useAuth from './hooks/useAuth';
import { setupInterceptors } from './services/api';

// Setup fetch interceptors for token refresh
setupInterceptors();

// This component wraps routes that should only be accessible when logged in
const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-neutral-50">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-center mt-4 text-neutral-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Dashboard router based on user role
const DashboardRouter = () => {
  const { user, loading } = useAuth();
  
  // Show loading while checking auth
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-neutral-50">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-center mt-4 text-neutral-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  // Redirect admin roles to admin panel
  if (['superadmin', 'admin'].includes(user?.role)) {
    return <Navigate to="/admin-panel/dashboard" replace />;
  }
  // Redirect terminal roles to dashboardTerminal — only once the role is actually
  // loaded, so an undefined role doesn't fall through here and mis-route.
  if (user?.role && user.role !== 'user') {
    return <Navigate to="/dashboardTerminal" replace />;
  }

  return <RegistrationDashboard />;
};

// Terminal dashboard route - only accessible for non-user roles
const DashboardTerminalRoute = () => {
  const { user, loading } = useAuth();
  
  // Show loading while checking auth
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-neutral-50">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-center mt-4 text-neutral-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  // If user role is 'user', redirect to regular dashboard
  if (user?.role === 'user') {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Show terminal dashboard for non-user roles
  return <TerminalDashboard />;
};

// Wrapper for routes that should only show DashboardLayout for regular users
const DashboardLayoutWrapper = ({ children }) => {
  const { user, loading } = useAuth();
  
  // Show loading while checking auth
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-neutral-50">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-center mt-4 text-neutral-600">Loading...</p>
        </div>
      </div>
    );
  }
  
  // If role is NOT 'user', show without DashboardLayout
  if (user?.role !== 'user') {
    return children;
  }
  
  // For regular users, show with DashboardLayout
  return <DashboardLayout>{children}</DashboardLayout>;
};

// Route guard for superadmin / admin only
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen bg-neutral-50">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-center mt-4 text-neutral-600">Loading...</p>
        </div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" />;
  if (!['superadmin', 'admin'].includes(user?.role)) return <Navigate to="/dashboard" />;
  return children;
};

const App = () => {
  const auth = useAuth();
  
  useEffect(() => {
    // console.log('Auth state:', { 
    //   isAuthenticated: auth.isAuthenticated, 
    //   loading: auth.loading,
    //   user: auth.user 
    // }); //Use for development
  }, [auth.isAuthenticated, auth.loading, auth.user]);
  
  return (
    <LanguageProvider>
      <NotificationProvider>
        <Router>
          <Routes>
            {/* Login route */}
            <Route path="/login" element={<LoginPage />} />
            
            {/* Landing Page - always use public layout */}
            <Route 
              path="/" 
              element={
                <PublicLayout>
                  <LandingPage />
                </PublicLayout>
              } 
            />
            
            {/* Consultation page - always use public layout */}
            <Route 
              path="/consultation" 
              element={
                <PublicLayout>
                  <ConsultationPage />
                </PublicLayout>
              } 
            />
            
            {/* Insurance page - always use public layout */}
            <Route 
              path="/insuranceGE" 
              element={
                <PublicLayout>
                  <InsurancePageGE />
                </PublicLayout>
              } 
            />

            {/* Queue to Russia page - public route */}
            <Route 
              path="/queue-russia" 
              element={
                <PublicLayout>
                  <QueueToRussiaPage />
                </PublicLayout>
              } 
            />
            
            {/* Private routes - only accessible when logged in */}
            <Route 
              path="/dashboard" 
              element={
                <PrivateRoute>
                  <DashboardLayoutWrapper>
                    <DashboardRouter />
                  </DashboardLayoutWrapper>
                </PrivateRoute>
              } 
            />
            
            {/* Terminal dashboard for non-user roles */}
            <Route 
              path="/dashboardTerminal" 
              element={
                <PrivateRoute>
                  {/* Check if user is not 'user' role, otherwise redirect */}
                  <DashboardTerminalRoute />
                </PrivateRoute>
              } 
            />
            
            {/* Invoices route */}
            <Route 
              path="/invoices" 
              element={
                <PrivateRoute>
                  <DashboardLayoutWrapper>
                    <InvoicePage />
                  </DashboardLayoutWrapper>
                </PrivateRoute>
              } 
            />
		    <Route 
              path="/insurance" 
              element={
                <PrivateRoute>
                  <DashboardLayoutWrapper>
                    <InsurancePage />
                  </DashboardLayoutWrapper>
                </PrivateRoute>
              } 
            />
		    <Route 
              path="/vehicleFleet" 
              element={
                <PrivateRoute>
                  <DashboardLayoutWrapper>
                    <VehicleFleetPage />
                  </DashboardLayoutWrapper>
                </PrivateRoute>
              } 
            />            
            {/* Admin panel routes */}
            <Route path="/admin-panel" element={<AdminRoute><AdminLayout><Navigate to="/admin-panel/dashboard" replace /></AdminLayout></AdminRoute>} />
            <Route path="/admin-panel/dashboard" element={<Navigate to="/admin-panel/dashboard/stats" replace />} />
            <Route path="/admin-panel/dashboard/:tab" element={<AdminRoute><AdminLayout><AdminDashboard /></AdminLayout></AdminRoute>} />
            <Route path="/admin-panel/registrations" element={<AdminRoute><AdminLayout><AdminRegistrations /></AdminLayout></AdminRoute>} />
            <Route path="/admin-panel/invoices" element={<AdminRoute><AdminLayout><AdminInvoices /></AdminLayout></AdminRoute>} />
            <Route path="/admin-panel/insurance" element={<AdminRoute><AdminLayout><AdminInsurance /></AdminLayout></AdminRoute>} />
            <Route path="/admin-panel/preorders" element={<AdminRoute><AdminLayout><AdminPreorders /></AdminLayout></AdminRoute>} />
            <Route path="/admin-panel/vehicles" element={<AdminRoute><AdminLayout><AdminVehicleFleet /></AdminLayout></AdminRoute>} />
            <Route path="/admin-panel/history" element={<AdminRoute><AdminLayout><AdminHistory /></AdminLayout></AdminRoute>} />

            {/* Catch-all - redirect to home */}
            <Route
              path="*"
              element={<Navigate to="/" replace />}
            />
          </Routes>
        </Router>
      </NotificationProvider>
    </LanguageProvider>
  );
};

export default App;